import React, { useCallback, useRef } from 'react';
import {
    Button,
    Checkbox,
    ProgressBar,
    Table,
    TableBody,
    TableCell,
    TableCellLayout,
    TableColumnDefinition,
    TableColumnId,
    TableHeader,
    TableHeaderCell,
    TableHeaderCellProps,
    TableRow,
    Tooltip,
    createTableColumn,
    makeStyles,
    tokens,
    useTableFeatures,
    useTableSort,
} from '@fluentui/react-components';
import { DocumentPdfRegular, DocumentTextRegular, FluentIconsProps } from '@fluentui/react-icons';
import { useProGPTDocuments } from '../hooks/useProGPTDocuments';
import { useNexusDocuments } from '../hooks/useNexusDocuments';
import { ChatDocument } from '../../../domain/documents/models/ChatDocument';
import { AlwaysOnScrollBarStyles, customColors, customFonts, SharedStyles } from '../../../styles';
import { formatFileSize, timestampToDateString } from '../../utils/TextUtils';
import { ConfirmDialog } from '../../utils/ConfirmDiaglog';
import { handleDuplicate, sanitizeFileName } from '../../utils/FileUtils';
import { alerts } from '../../../shared/ui/Alerts';
import { NexusAgent } from '../../../domain/agents/models/Agent';
import { ChatProviderType } from '../../../domain/chat/models/ChatProviderType';
import { UploadStatusIndicator } from './UploadStatusIndicator';
import { FileUploadStatus } from '../hooks/useProGPTDocuments';

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        paddingLeft: '10px',
        paddingRight: '10px',
        margin: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalM}`,
        ...SharedStyles.scroll,
    },
    functional: {
        display: 'flex',
        flexDirection: 'row',
        marginBottom: tokens.spacingVerticalS,
        justifyContent: 'right',
    },
    uploadButton: {
        marginRight: tokens.spacingHorizontalS,
        fontFamily: customFonts.Lato,
        fontSize: '12px',
        borderRadius: '5px',
        border: `1px solid ${customColors.protivitiOrange}`,
        padding: '10px 45px',
    },
    table: {
        backgroundColor: tokens.colorNeutralBackground1,
        width: '100%',
        tableLayout: 'auto',
    },
    span: {
        fontFamily: customFonts.Lato,
        fontWeight: '400',
        fontSize: '12px',
        lineHeight: tokens.lineHeightBase200,
        marginBottom: '5px',
        color: 'black',
    },
    row: {
        fontFamily: customFonts.Lato,
        fontWeight: '400',
        fontSize: '12px',
        color: 'black',
        '& > td': {
            minWidth: '125px',
            width: 'fit-content',
            maxWidth: '400px',
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
            wordBreak: 'break-all',
        },
    },
    column: {
        '& > th > div': {
            fontFamily: customFonts.Lato,
            fontWeight: '700',
            fontSize: '14px',
            color: customColors.summarizeButtonDarker,
        },
    },
    title: {
        fontFamily: customFonts.TitleFont,
        fontWeight: '500',
        fontSize: '19px',
        lineHeight: '22.8px',
    },
    container: {
        height: '100%',
        ...AlwaysOnScrollBarStyles,
    },
});

interface DocumentsTabProps {
    sessionId: string;
    resolvedAgent: NexusAgent | null;
}

interface TableItem {
    id: string;
    name: {
        label: string;
        icon: JSX.Element;
        url?: string;
    };
    createdOn: {
        label: string;
        timestamp: number;
    };
    tokens: number;
    size: number;
    fileUploadStatus?: FileUploadStatus;
}

const MAX_FILE_COUNT_PROGPT = 100;
const MAX_FILE_COUNT_NEXUS = 15;
const MAX_FILE_SIZE_MB_PROGPT = 30;
const MAX_FILE_SIZE_MB_NEXUS = 20;
const MAX_FILE_SIZE_BYTES_PROGPT = MAX_FILE_SIZE_MB_PROGPT * 1024 * 1024;
const MAX_FILE_SIZE_BYTES_NEXUS = MAX_FILE_SIZE_MB_NEXUS * 1024 * 1024;

const fileSearchAllowedExtensions = ['doc', 'docx', 'ppt', 'pptx', 'txt', 'pdf', 'png', 'jpeg', 'jpg'];
const codeInterpreterAllowedExtensions = [
    'csv',
        'xlsx',
        'docx',
        'html',
        'java',
        'json',
        'md',
        'pdf',
        'php',
        'pptx',
        'py',
        'rb',
        'tex',
        'txt',
        'css',
        'jpeg',
        'jpg',
        'gif',
        'png',
        'tar',
        'ts',
        'xml',
        'zip',
        'c',
        'cpp',
        'cs',
        'doc',
        'docx',
        'html',
        'java',
        'json',
        'md',
        'pdf',
        'pptx',
        'py',
        'rb',
        'tex',
        'txt',
        'css',
        'js',
        'sh',
        'ts',
];

export const DocumentsTab: React.FC<DocumentsTabProps> = ({ sessionId, resolvedAgent }) => {
    const classes = useStyles();
    const documentFileRef = useRef<HTMLInputElement | null>(null);
    const [isDialogOpen, setIsDialogOpen] = React.useState(false);

    const isNexusAgent = resolvedAgent != null && resolvedAgent.providerType === ChatProviderType.Nexus;
    

    const ALLOWED_EXTENSIONS = (isNexusAgent && resolvedAgent.allowCodeInterpreterAtConversationLevel) ? codeInterpreterAllowedExtensions : fileSearchAllowedExtensions;
    const MAX_FILE_COUNT = isNexusAgent ? MAX_FILE_COUNT_NEXUS : MAX_FILE_COUNT_PROGPT;
    const MAX_FILE_SIZE_MB = isNexusAgent ? MAX_FILE_SIZE_MB_NEXUS : MAX_FILE_SIZE_MB_PROGPT;
    const MAX_FILE_SIZE_BYTES = isNexusAgent ? MAX_FILE_SIZE_BYTES_NEXUS : MAX_FILE_SIZE_BYTES_PROGPT;

    const acceptedFileTypes = (isNexusAgent && resolvedAgent.allowCodeInterpreterAtConversationLevel)
        ? codeInterpreterAllowedExtensions.map((ext) => `.${ext}`).join(',')
        : fileSearchAllowedExtensions.map((ext) => `.${ext}`).join(',');


    const {
        documents,
        isImporting,
        uploadStatus,
        fileUploadStatuses,
        selectedDocumentIds,
        isDeleting,
        onUploadDocuments,
        onDeleteDocuments,
        onSelectDocument,
        onSelectAllDocuments,
    } = isNexusAgent 
        ? useNexusDocuments(sessionId, resolvedAgent) 
        : useProGPTDocuments(sessionId);

    const existingFileNames = React.useMemo(() => {
        return new Set(documents.map((doc) => doc.name));
    }, [documents]);

    const { columns, rows } = useTable(documents, fileUploadStatuses, selectedDocumentIds, onSelectDocument, isNexusAgent);

    const handleImport = useCallback(async () => {
        const files = documentFileRef.current?.files;
        if (!files || files.length === 0) return;

        if (files.length > MAX_FILE_COUNT || files.length + documents.length > MAX_FILE_COUNT) {
            alerts.error(
                `You can only upload up to ${MAX_FILE_COUNT} files. Your current file count is: ${files.length + documents.length}. Please try uploading with fewer files.`,
            );
            if (documentFileRef.current?.value) {
                documentFileRef.current.value = '';
            }
            return;
        }

        const filesArray = Array.from(files);

        // Validate and sanitize files
        for (let i = filesArray.length - 1; i >= 0; i--) {
            const file = filesArray[i];

            // Check file size
            if (file.size > MAX_FILE_SIZE_BYTES) {
                alerts.error(`File size exceeds the ${MAX_FILE_SIZE_MB} MB limit. Please choose a smaller file.`);
                filesArray.splice(i, 1);
                continue;
            }

            // Check file extension
            const fileExtension = file.name.split('.').pop()?.toLowerCase();
            if (!fileExtension || !ALLOWED_EXTENSIONS.includes(fileExtension)) {
                alerts.error(`This file type is not allowed. Please upload a different file.`);
                filesArray.splice(i, 1);
                continue;
            }

            // Sanitize and handle duplicates
            let sanitizedName = sanitizeFileName(file.name);
            if (existingFileNames.has(sanitizedName)) {
                sanitizedName = handleDuplicate(existingFileNames, sanitizedName, filesArray);
            }
            filesArray[i] = new File([file], sanitizedName, { type: file.type });
        }

        if (filesArray.length === 0) {
            if (documentFileRef.current?.value) {
                documentFileRef.current.value = '';
            }
            return;
        }

        await onUploadDocuments(filesArray);

        if (documentFileRef.current?.value) {
            documentFileRef.current.value = '';
        }
    }, [documents.length, existingFileNames, onUploadDocuments, ALLOWED_EXTENSIONS, MAX_FILE_SIZE_BYTES, MAX_FILE_SIZE_MB]);

    const handleMultiDelete = useCallback(() => {
        setIsDialogOpen(true);
    }, []);

    const onYesDialogClick = useCallback(async () => {
        setIsDialogOpen(false);
        await onDeleteDocuments(Array.from(selectedDocumentIds));
    }, [onDeleteDocuments, selectedDocumentIds]);

    const onNoDialogClick = useCallback(() => {
        setIsDialogOpen(false);
    }, []);

    return (
        <div className={classes.container}>
            <div className={classes.root}>
                <span className={classes.span}>
                    {isNexusAgent
                        ? `You can upload up to ${MAX_FILE_COUNT} documents. The maximum size for each document is ${MAX_FILE_SIZE_MB} MB.`
                        : `You can upload up to ${MAX_FILE_COUNT} documents. The maximum size for each document is ${MAX_FILE_SIZE_MB} MB. Supported file types are Microsoft Word, PowerPoint, Portable Document Format (PDF), Text (TXT), JPG, JPEG, and PNG.`}
                </span>

                <div className={classes.functional}>
                    <input
                        type="file"
                        ref={documentFileRef}
                        style={{ display: 'none' }}
                        accept={acceptedFileTypes}
                        multiple={true}
                        onChange={handleImport}
                    />
                    <Tooltip content="Embed file into chat session" relationship="label">
                        <Button
                            className={classes.uploadButton}
                            disabled={isImporting > 0 || documents.length >= MAX_FILE_COUNT}
                            onClick={() => documentFileRef.current?.click()}
                        >
                            Upload
                        </Button>
                    </Tooltip>

                    <Tooltip content="Delete selected files" relationship="label">
                        <Button
                            className={classes.uploadButton}
                            disabled={isDeleting || selectedDocumentIds.size === 0}
                            onClick={handleMultiDelete}
                        >
                            {isDeleting ? 'Deleting' : 'Delete'}
                        </Button>
                    </Tooltip>

                    <Tooltip content="Select all files" relationship="label">
                        <Button className={classes.uploadButton} onClick={onSelectAllDocuments}>
                            Select All
                        </Button>
                    </Tooltip>
                </div>

                <UploadStatusIndicator status={uploadStatus} fileCount={isImporting > 0 ? isImporting : undefined} />

                <Table aria-label="Documents table" className={classes.table}>
                    <TableHeader>
                        <TableRow className={classes.column}>
                            {columns.map((column) => column.renderHeaderCell())}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {rows.map((item) => (
                            <TableRow className={classes.row} key={item.id}>
                                {columns.map((column) => column.renderCell(item))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                {isDialogOpen && (
                    <ConfirmDialog
                        message={'Are you sure you want to delete the selected document(s)?'}
                        onYesDialog={() => onYesDialogClick()}
                        onNoDialog={() => onNoDialogClick()}
                    />
                )}
            </div>
        </div>
    );
};

function useTable(
    documents: ChatDocument[],
    fileUploadStatuses: Map<string, FileUploadStatus>,
    selectedDocumentIds: Set<string>,
    onSelectDocument: (id: string, selected: boolean) => void,
    isNexusAgent: boolean,
) {
    const headerSortProps = (columnId: TableColumnId): TableHeaderCellProps => ({
        onClick: (e: React.MouseEvent) => {
            toggleColumnSort(e, columnId);
        },
        sortDirection: getSortDirection(columnId),
    });

    const columns: Array<TableColumnDefinition<TableItem>> = [
        createTableColumn<TableItem>({
            columnId: 'select',
            renderHeaderCell: () => (
                <TableHeaderCell key="select" {...headerSortProps('select')}>
                    Select
                </TableHeaderCell>
            ),
            renderCell: (item) => (
                <TableCell key={`${item.id}-select`}>
                    <Checkbox
                        checked={selectedDocumentIds.has(item.id)}
                        disabled={!item.id || (item.id.startsWith('in-progress') && item.fileUploadStatus !== 'error')}
                        onChange={(_, data) => onSelectDocument(item.id, data.checked === true)}
                    />
                </TableCell>
            ),
        }),
        createTableColumn<TableItem>({
            columnId: 'name',
            renderHeaderCell: () => (
                <TableHeaderCell key="name" {...headerSortProps('name')}>
                    Name
                </TableHeaderCell>
            ),
            renderCell: (item) => (
                <TableCell key={item.id}>
                    <TableCellLayout media={item.name.icon}>
                        {item.name.url ? <a href={item.name.url}>{item.name.label}</a> : item.name.label}
                    </TableCellLayout>
                </TableCell>
            ),
            compare: (a, b) => {
                const comparison = a.name.label.localeCompare(b.name.label);
                return getSortDirection('name') === 'ascending' ? comparison : comparison * -1;
            },
        }),
        createTableColumn<TableItem>({
            columnId: 'createdOn',
            renderHeaderCell: () => (
                <TableHeaderCell key="createdOn" {...headerSortProps('createdOn')}>
                    Created On
                </TableHeaderCell>
            ),
            renderCell: (item) => (
                <TableCell key={item.createdOn.timestamp} title={new Date(item.createdOn.timestamp).toLocaleString()}>
                    {item.id && item.id.startsWith('in-progress') ? 'N/A' : item.createdOn.label}
                </TableCell>
            ),
            compare: (a, b) => {
                const comparison = a.createdOn.timestamp > b.createdOn.timestamp ? 1 : -1;
                return getSortDirection('createdOn') === 'ascending' ? comparison : comparison * -1;
            },
        }),
        createTableColumn<TableItem>({
            columnId: 'size',
            renderHeaderCell: () => (
                <TableHeaderCell key="size" {...headerSortProps('size')}>
                    {isNexusAgent ? 'Token Count' : 'Size'}
                </TableHeaderCell>
            ),
            renderCell: (item) => (
                <TableCell key={`${item.id}-size`}>
                    {item.id && item.id.startsWith('in-progress')
                        ? 'N/A'
                        : isNexusAgent
                          ? item.tokens
                          : formatFileSize(item.size)}
                </TableCell>
            ),
            compare: (a, b) => {
                const comparison = isNexusAgent ? a.tokens - b.tokens : a.size - b.size;
                return getSortDirection('size') === 'ascending' ? comparison : comparison * -1;
            },
        }),
        createTableColumn<TableItem>({
            columnId: 'progress',
            renderHeaderCell: () => (
                <TableHeaderCell key="progress" {...headerSortProps('progress')}>
                    Progress
                </TableHeaderCell>
            ),
            renderCell: (item) => {
                const isInFlight = item.id.startsWith('in-progress');
                let value: number | undefined = 1;
                let color: 'brand' | 'success' | 'error' = 'success';
                if (isInFlight) {
                    if (item.fileUploadStatus === 'error') {
                        color = 'error';
                    } else if (item.fileUploadStatus === 'uploading') {
                        value = undefined;
                        color = 'brand';
                    } else {
                        color = 'success';
                    }
                }
                return (
                    <TableCell key={`${item.id}-progress`}>
                        <ProgressBar max={1} value={value} shape="rounded" thickness="large" color={color} />
                    </TableCell>
                );
            },
        }),
    ];

    const uploadingItems: TableItem[] = Array.from(fileUploadStatuses.entries())
        .filter(([name]) => !documents.some((doc) => doc.name === name))
        .map(([name, status]) => ({
            id: `in-progress-${name}`,
            name: { label: name, icon: getFileIconByFileExtension(name) },
            createdOn: { label: 'N/A', timestamp: 0 },
            tokens: 0,
            size: 0,
            fileUploadStatus: status,
        }));

    const items: TableItem[] = [
        ...uploadingItems,
        ...documents
            .filter((doc) => doc && doc.id)
            .map((doc) => ({
                id: doc.id,
                name: {
                    label: doc.name ?? 'Unknown',
                    icon: getFileIconByFileExtension(doc.name),
                },
                createdOn: {
                    label: timestampToDateString(doc.createdOn),
                    timestamp: doc.createdOn ?? 0,
                },
                tokens: doc.tokens ?? 0,
                size: doc.size ?? 0,
                fileUploadStatus: undefined,
            })),
    ];

    const {
        sort: { getSortDirection, toggleColumnSort, sortColumn },
    } = useTableFeatures(
        {
            columns,
            items,
        },
        [
            useTableSort({
                defaultSortState: { sortColumn: 'createdOn', sortDirection: 'descending' },
            }),
        ],
    );

    if (sortColumn) {
        items.sort((a, b) => {
            const compare = columns.find((column) => column.columnId === sortColumn)?.compare;
            return compare?.(a, b) ?? 0;
        });
    }

    return { columns, rows: items };
}

export function getFileIconByFileExtension(fileName: string, props: FluentIconsProps = {}) {
    if (!fileName) {
        return <DocumentTextRegular {...props} />;
    }

    const lastDotIndex = fileName.toLowerCase().lastIndexOf('.');
    if (lastDotIndex === -1) {
        return <DocumentTextRegular {...props} />;
    }

    const extension = fileName.toLowerCase().substring(lastDotIndex + 1);
    if (extension === 'pdf') {
        return <DocumentPdfRegular {...props} />;
    }
    return <DocumentTextRegular {...props} />;
}

export default DocumentsTab;
